import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send, ListChecks } from "lucide-react";
import { toast } from "sonner";

interface ProfileEmailDialogProps {
  profileId: string;
  profileName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missingFields?: string[];
}

export function ProfileEmailDialog({
  profileId,
  profileName,
  open,
  onOpenChange,
  missingFields = [],
}: ProfileEmailDialogProps) {
  const queryClient = useQueryClient();
  
  const [subject, setSubject] = useState(
    `EAB Connect – Additional Information Needed for Your Profile`
  );
  const [body, setBody] = useState(
    `Hello ${profileName.split(" ")[0] || "there"},\n\nThank you for signing up for EAB Connect!\n\nBefore we can fully activate your profile, we need a bit more information from you. Please log in to your account and complete the missing sections.\n\nIf you have any questions, feel free to use the Ask Nedra button in the platform.\n\nBest regards,\nThe EAB Connect Team`
  );

  const insertMissingFields = () => {
    if (missingFields.length === 0) {
      toast.info("No missing fields to insert");
      return;
    }
    
    const fieldsList = missingFields.map((f) => `• ${f}`).join("\n");
    const insertText = `\n\nThe following information is needed:\n${fieldsList}\n`;
    
    // Insert after the first paragraph
    const paragraphs = body.split("\n\n");
    if (paragraphs.length > 1) {
      paragraphs.splice(1, 0, insertText.trim());
      setBody(paragraphs.join("\n\n"));
    } else {
      setBody(body + insertText);
    }
  };

  const sendEmail = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-send-profile-email", {
        body: {
          profileId,
          subject,
          bodyText: body,
          templateType: "request_info",
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile_admin_emails", profileId] });
      toast.success("Email sent successfully!");
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error("Failed to send email: " + error.message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Email Applicant</DialogTitle>
          <DialogDescription>
            Send an email to {profileName} regarding their profile application
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="body">Message</Label>
              {missingFields.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={insertMissingFields}
                >
                  <ListChecks className="h-4 w-4 mr-2" />
                  Insert Missing Fields List
                </Button>
              )}
            </div>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body..."
              rows={12}
              className="font-mono text-sm"
            />
          </div>

          {missingFields.length > 0 && (
            <div className="text-sm text-muted-foreground">
              <p className="font-medium">Missing fields detected:</p>
              <ul className="list-disc list-inside">
                {missingFields.slice(0, 5).map((field) => (
                  <li key={field}>{field}</li>
                ))}
                {missingFields.length > 5 && (
                  <li>...and {missingFields.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => sendEmail.mutate()}
            disabled={sendEmail.isPending || !subject.trim() || !body.trim()}
          >
            {sendEmail.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
